"use client";

import { MediaGallery, GalleryMediaCategory, GalleryMediaItem, useMediaGallery } from "@/components/common/gallery";
import { GalleryCategories, GalleryMediaCounter, GalleryNavigationButtons } from "@/components/common/gallery/overlays";
import { prepareGalleryMedia } from "@/utils/sup/gallery";
import * as React from "react";
import { type GalleryProps, getSortedCategories } from "./shared";

// Re-export GalleryProps for external consumers
export type { GalleryProps };

// Internal component that uses gallery context to sync category
function GalleryContent({
	media,
	sortedCategories,
	selectedCategory,
	setSelectedCategory,
}: {
	media: GalleryMediaItem[];
	sortedCategories: GalleryMediaCategory[];
	selectedCategory: GalleryMediaCategory | undefined;
	setSelectedCategory: (category: GalleryMediaCategory | undefined) => void;
}) {
	const { currentIndex, goToPrevious, goToNext } = useMediaGallery();

	// Sync selected category with current media item
	React.useEffect(() => {
		const currentItem = media[currentIndex];
		if (currentItem?.space) {
			const currentCategory = sortedCategories.find(cat => cat.code === currentItem.space);
			if (currentCategory && currentCategory.code !== selectedCategory?.code) {
				setSelectedCategory(currentCategory);
			}
		}
	}, [currentIndex, media, sortedCategories, selectedCategory, setSelectedCategory]);

	const showCategories = sortedCategories.length > 0;
	const canGoPrevious = currentIndex > 0;
	const canGoNext = currentIndex < media.length - 1;

	return (
		<>
			{/* Media Counter - hidden when category nav is visible */}
			{!showCategories && <GalleryMediaCounter showMediaName={true} />}

			{/* Navigation Buttons */}
			<GalleryNavigationButtons
				onPrevious={goToPrevious}
				onNext={goToNext}
				canGoPrevious={canGoPrevious}
				canGoNext={canGoNext}
			/>

			{/* Category Navigation */}
			{showCategories && (
				<GalleryCategories
					categories={sortedCategories}
					selectedCategory={selectedCategory}
					onCategoryChange={setSelectedCategory}
				/>
			)}
		</>
	);
}

export const GalleryLarge = React.memo(function GalleryLarge({
	mediaList = [],
	categories,
	coverImage,
	projectId,
	type,
	className,
	isLoading = false,
	shouldPrioritizeCoverImage = true,
	sizes = "80dvw",
	onMainImageClick,
	aspectRatio = "default",
	videoAutoplayOnLoad = false,
}: GalleryProps) {
	// Convert to GalleryMediaItem format and apply sorting
	const sortedMedia = React.useMemo(() => {
		return prepareGalleryMedia({
			mediaList,
			coverImage,
			projectId,
			type,
			shouldPrioritizeCoverImage,
		});
	}, [mediaList, coverImage, projectId, type, shouldPrioritizeCoverImage]);

	// Sort categories by unitSpaces priority and filter to only those with media
	const sortedCategories = React.useMemo(() => {
		return getSortedCategories(categories, sortedMedia);
	}, [categories, sortedMedia]);

	// Track selected category - select the first category from sorted list
	const [selectedCategory, setSelectedCategory] = React.useState<GalleryMediaCategory | undefined>(sortedCategories[0]);

	if (isLoading) {
		return (
			<div className="w-full h-full bg-gray-100 animate-pulse">
				<div className="w-full h-full bg-gray-200"></div>
			</div>
		);
	}

	if (!sortedMedia || sortedMedia.length === 0) {
		return (
			<div className="flex items-center justify-center w-full h-96 bg-gray-100">
				<div className="text-center">
					<div className="w-16 h-16 bg-gray-300 mx-auto mb-4" />
					<p className="text-gray-500">No media to show</p>
				</div>
			</div>
		);
	}

	// Determine aspect ratio classes
	const aspectRatioClasses =
		aspectRatio === "square"
			? "aspect-square"
			: aspectRatio === "fixed-height"
				? "aspect-square @4xl:aspect-auto @4xl:min-h-[500px]"
				: ""; // default: no constraint, inherits from wrapper completely

	return (
		<div className={`bg-white overflow-hidden h-full relative ${aspectRatioClasses} ${className || ""}`}>
			<MediaGallery
				media={sortedMedia}
				className="w-full h-full"
				showNavigationButtons={true}
				showMediaCounter={false} // We'll use custom counter
				enableKeyboardNavigation={true}
				enableDoubleTapFullscreen={true}
				autoLoop={true}
				sizes={sizes}
				onMainImageClick={onMainImageClick}
				videoAutoplayOnLoad={videoAutoplayOnLoad}
			>
				<GalleryContent
					media={sortedMedia}
					sortedCategories={sortedCategories}
					selectedCategory={selectedCategory}
					setSelectedCategory={setSelectedCategory}
				/>
			</MediaGallery>
		</div>
	);
});
